import type { LedgerItem, LedgerResponse, PriceSource } from '@brimstone/shared';
import type { ReactNode } from 'react';
import { AreaChart } from '../../components/AreaChart';
import { DataGrid, DataRow, EmptyState, KpiTile, PageLayout, Panel } from '../../components/ui';
import { formatMoney } from '../../lib/money';
import { formatDate, useNow } from '../../lib/time';
import { useLedger } from '../../lib/tracking';

const DAYS = 90;
const DAY_MS = 86_400_000;
const COLUMNS = 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

const SOURCE: Record<PriceSource, string> = { ah: 'AH', vendor: 'VENDOR', none: 'NO PRICE' };

const itemName = (i: LedgerItem) => i.name ?? `Item ${i.itemId}`;

function Chart({ data, now }: { data: LedgerResponse; now: number }) {
  // Up to today, or the newest point when the server runs ahead (dev fixtures).
  const today = new Date(now).toISOString().slice(0, 10);
  const newest = data.points.at(-1)?.day ?? today;
  const to = newest > today ? newest : today;
  const from = new Date(Date.parse(`${to}T00:00:00Z`) - (DAYS - 1) * DAY_MS)
    .toISOString()
    .slice(0, 10);
  return (
    <Panel title="Net worth" meta={`LAST ${DAYS} DAYS`}>
      {data.points.length === 0 ? (
        <EmptyState>No days recorded yet — one is kept per day with an upload.</EmptyState>
      ) : (
        <div className="px-2 py-3">
          <AreaChart
            label={`Net worth over the last ${DAYS} days`}
            points={data.points.map((p) => ({ day: p.day, value: p.totalCopper }))}
            from={from}
            to={to}
            format={(v) => formatMoney(v, true)}
          />
        </div>
      )}
    </Panel>
  );
}

function Characters({ data }: { data: LedgerResponse }) {
  return (
    <Panel title="By character">
      {data.characters.length === 0 ? (
        <EmptyState>No gold observed yet.</EmptyState>
      ) : (
        <DataGrid
          label="Net worth by character"
          columns={COLUMNS}
          header={['Character', 'Gold', 'Items', 'Total']}
        >
          {data.characters.map((c) => (
            <DataRow key={c.id} columns={COLUMNS}>
              <span role="cell" className="text-name">
                {c.name}
              </span>
              <span role="cell" className="font-mono text-secondary">
                {formatMoney(c.goldCopper)}
              </span>
              <span role="cell" className="font-mono text-secondary">
                {formatMoney(c.itemsCopper)}
              </span>
              <span role="cell" className="font-mono">
                {formatMoney(c.totalCopper)}
              </span>
            </DataRow>
          ))}
        </DataGrid>
      )}
    </Panel>
  );
}

function ItemList({ items }: { items: LedgerItem[] }) {
  return (
    <ul className="m-0 flex list-none flex-col px-4 py-2 text-compact">
      {items.map((i) => (
        <li
          key={i.itemId}
          className="flex items-baseline justify-between gap-3 border-b border-line-soft py-2 last:border-b-0"
        >
          <span className="min-w-0">
            <span className={i.name ? undefined : 'text-muted'}>{itemName(i)}</span>{' '}
            <span className="font-mono text-muted">×{i.count}</span>
          </span>
          <span className="flex shrink-0 items-baseline gap-2 font-mono">
            {i.source !== 'none' && <span>{formatMoney(i.valueCopper)}</span>}
            <span className="text-tag text-muted">{SOURCE[i.source]}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Net worth now, over 90 days, by character, and the top items (SPEC §6.10): the Gold page's
 * Overview (M15), with its own panels after the Ledger's.
 */
export function LedgerPage({ main, side }: { main?: ReactNode; side?: ReactNode } = {}) {
  const q = useLedger(DAYS);
  const now = useNow(3_600_000);
  const data = q.data;
  const money = (v: number | undefined) => (v === undefined ? '—' : formatMoney(v, true));
  return (
    <PageLayout
      kpis={
        <>
          <KpiTile label="Net worth" value={money(data?.current?.totalCopper)} />
          <KpiTile label="Gold" value={money(data?.current?.goldCopper)} />
          <KpiTile label="Items" value={money(data?.current?.itemsCopper)} />
        </>
      }
      main={
        !data ? (
          <Panel title="Ledger">
            <EmptyState>{q.isError ? 'Couldn’t load the Ledger.' : 'Loading…'}</EmptyState>
          </Panel>
        ) : (
          <>
            <Chart data={data} now={now} />
            {main}
            <Characters data={data} />
          </>
        )
      }
      side={
        data && (
          <>
            {side}
            <Panel
              title="Top items"
              level="side"
              meta={
                data.pricesAsOf ? `AH ${formatDate(data.pricesAsOf).toUpperCase()}` : 'NO AH SCAN'
              }
            >
              {data.topItems.length === 0 ? (
                <EmptyState>No priced items yet.</EmptyState>
              ) : (
                <ItemList items={data.topItems} />
              )}
            </Panel>
            <Panel title="Unpriced" level="side" meta={String(data.unpriced.length)}>
              {data.unpriced.length === 0 ? (
                <EmptyState>Every item has an AH or vendor price.</EmptyState>
              ) : (
                <>
                  <p className="m-0 px-4 pt-3 text-secondary text-muted">
                    No AH or vendor price: counted as 0.
                  </p>
                  <ItemList items={data.unpriced} />
                </>
              )}
              {data.boundLeftOut > 0 && (
                <p className="m-0 border-t border-line px-4 py-3 text-secondary text-muted">
                  {data.boundLeftOut} bound {data.boundLeftOut === 1 ? 'stack' : 'stacks'} left out:
                  they can’t be sold.
                </p>
              )}
            </Panel>
          </>
        )
      }
    />
  );
}
